import React, { useEffect, useState } from "react";
import { apiGet, apiPut, apiPost } from "../../../api";
import { FiCheck, FiX } from "react-icons/fi";
import notificationService from "../../../notificationService"; // 1. IMPORT THE SERVICE
import "../styles/Inbox.css";

const anonymizeEmail = (email) => (email && email.includes('@') ? email.split('@')[0] : email);

// ... (RequestCard and ConfirmationModal components are unchanged) ...
function RequestCard({ request, type, onRespond }) {
  const isReceived = type === 'received';
  const user = isReceived ? request.fromUser : request.toUser;
  const userName = isReceived ? request.senderName : anonymizeEmail(user?.email);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  return (
    <div className={`request-card status-${request.status.toLowerCase()}`}>
      <div className="card-header">
        <div className="user-info">
          <strong>{isReceived ? 'From:' : 'To:'} {userName || '(name hidden)'}</strong>
        </div>
        <div className="timestamp">{formatDate(request.createdAt)}</div>
      </div>
      <div className="card-body">
        {isReceived && (
          <div className="sender-details">
            {[request.senderDegree, request.senderMajor].filter(Boolean).join(", ")}
            {request.senderYear ? ` • Year ${request.senderYear}` : ""}
          </div>
        )}
        {request.message && <p className="message-content">"{request.message}"</p>}
        <div className="shared-courses-info">
          Shared Courses: <strong>{request.sharedCourseCount ?? 0}</strong>
          {request.sharedCourseCodes?.length > 0 && (
            <span> ({request.sharedCourseCodes.join(", ")})</span>
          )}
        </div>
      </div>
      <div className="card-actions">
        {request.status === "PENDING" ? (
          isReceived ? (
            <>
              <button onClick={() => onRespond(request.id, "DECLINED")} className="btn btn-danger"><FiX /> Decline</button>
              <button onClick={() => onRespond(request.id, "ACCEPTED")} className="btn btn-primary"><FiCheck /> Accept</button>
            </>
          ) : (
            <button onClick={() => onRespond(request.id, "CANCELLED")} className="btn btn-secondary"><FiX /> Cancel</button>
          )
        ) : (
          <div className="status-tag">
            {request.status === 'ACCEPTED' && <FiCheck />}
            {request.status === 'DECLINED' && <FiX />}
            {request.status === 'CANCELLED' && <FiX />}
            {request.status}
          </div>
        )}
      </div>
    </div>
  );
}

function ConfirmationModal({ action, onConfirm, onCancel }) {
  const actionText = action === 'DECLINED' ? 'decline' : 'cancel';
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-content confirmation-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-header">Are you sure?</h3>
        <p>Are you sure you want to {actionText} this request?</p>
        <div className="modal-actions">
          <button onClick={onCancel} className="btn btn-secondary">No, keep it</button>
          <button onClick={onConfirm} className="btn btn-danger">Yes, {actionText}</button>
        </div>
      </div>
    </div>
  );
}


export default function Inbox() {
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [activeTab, setActiveTab] = useState('received');
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    const markViewedAndLoad = async () => {
      setLoading(true);
      try {
        await apiPost("/inbox/mark-viewed"); // Clears "newly accepted" on server
        const data = await apiGet("/inbox");
        setReceived(data.received || []);
        setSent(data.sent || []);
        // 2. DISPATCH THE SIGNAL to instantly update the sidebar
        notificationService.dispatch('notificationsUpdated');
      } catch (e) {
        setMsg(e.message || "Failed to load inbox");
      } finally {
        setLoading(false);
      }
    };
    markViewedAndLoad();
  }, []);

  useEffect(() => {
    const newlyAcceptedCount = sent.filter(r => r.status === 'ACCEPTED' && !r.viewedBySender).length;
    if (activeTab === 'sent' && newlyAcceptedCount > 0) {
      apiPost("/inbox/mark-viewed")
        .then(() => {
          setSent(currentSent =>
            currentSent.map(req =>
              req.status === 'ACCEPTED' ? { ...req, viewedBySender: true } : req
            )
          );
          notificationService.dispatch('notificationsUpdated');
        })
        .catch(err => console.error("Failed to mark notifications as viewed:", err));
    }
  }, [activeTab, sent]);


  const respond = async (id, action) => {
    try {
      await apiPut(`/connect/${id}/respond`, { action });
      const data = await apiGet("/inbox");
      setReceived(data.received || []);
      setSent(data.sent || []);
      notificationService.dispatch('notificationsUpdated'); // Also update after responding
      setConfirmAction(null);
    } catch (e) {
      setMsg(e.message || "Failed to respond");
    }
  };

  const handleActionClick = (id, action) => {
    if (action === 'CANCELLED' || action === 'DECLINED') {
      setConfirmAction({ id, action });
    } else {
      respond(id, action);
    }
  };

  const pendingReceivedCount = received.filter(r => r.status === 'PENDING').length;
  const newlyAcceptedCount = sent.filter(r => r.status === 'ACCEPTED' && !r.viewedBySender).length;

  return (
    <div className="inbox-container page-card">
      <div className="inbox-header">
        <h2>Inbox</h2>
        <div className="tabs">
          <button onClick={() => setActiveTab('received')} className={`tab-btn ${activeTab === 'received' ? 'active' : ''}`}>
            Received
            {pendingReceivedCount > 0 && <span className="notification-badge">{pendingReceivedCount}</span>}
          </button>
          <button onClick={() => setActiveTab('sent')} className={`tab-btn ${activeTab === 'sent' ? 'active' : ''}`}>
            Sent
            {newlyAcceptedCount > 0 && <span className="notification-badge">{newlyAcceptedCount}</span>}
          </button>
        </div>
      </div>
      {msg && <p className="status-message error">{msg}</p>}
      {loading ? ( <p className="loading-message">Loading inbox...</p> ) : (
        <div className="requests-list">
          {activeTab === 'received' && (received.length === 0 ? <p className="empty-message">You have no connection requests.</p> :
            received.map(r => <RequestCard key={r.id} request={r} type="received" onRespond={handleActionClick} />)
          )}
          {activeTab === 'sent' && (sent.length === 0 ? <p className="empty-message">You have not sent any requests.</p> :
            sent.map(r => <RequestCard key={r.id} request={r} type="sent" onRespond={handleActionClick} />)
          )}
        </div>
      )}
      {confirmAction && (
        <ConfirmationModal
          action={confirmAction.action}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => respond(confirmAction.id, confirmAction.action)}
        />
      )}
    </div>
  );
}